import api from '../config/axiosConfig';
import { API_BASE_URL } from '../utils/constants';

export enum SupplierReturnStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  COMPLETED = 'COMPLETED',
}

export enum SupplierReturnReason {
  DEFECTIVE = 'DEFECTIVE',
  WRONG_ITEM = 'WRONG_ITEM',
  EXCESS_STOCK = 'EXCESS_STOCK',
  EXPIRED = 'EXPIRED',
  OTHER = 'OTHER',
}

export interface SupplierReturnItem {
  id: number;
  item_id: number;
  item_name: string;
  item_sku: string;
  quantity: number;
  unit_price: number;
  subtotal: number;
}

export interface SupplierReturnResponse {
  id: number;
  return_number: string;
  supplier_id: number;
  supplier_name: string;
  purchase_order_id?: number;
  po_number?: string;
  reason: SupplierReturnReason;
  status: SupplierReturnStatus;
  notes?: string;
  total_amount: number;
  created_by: number;
  approved_by?: number;
  approved_at?: string;
  created_at: string;
  updated_at: string;
  items: SupplierReturnItem[];
}

export interface SupplierReturnCreate {
  supplier_id: number;
  purchase_order_id?: number;
  reason: SupplierReturnReason;
  notes?: string;
  items: {
    item_id: number;
    quantity: number;
    unit_price: number;
  }[];
}

export interface SupplierReturnUpdate {
  status?: SupplierReturnStatus;
  notes?: string;
}

const API_URL = `${API_BASE_URL}/supplier-returns`;

// Fetch all supplier returns
export const fetchSupplierReturns = async (params?: {
  supplier_id?: number;
  status?: SupplierReturnStatus;
  reason?: SupplierReturnReason;
  page?: number;
  page_size?: number;
}): Promise<SupplierReturnResponse[]> => {
  const response = await api.get<SupplierReturnResponse[]>(API_URL, { params });
  return response.data;
};

// Get supplier return by ID
export const getSupplierReturnById = async (id: number): Promise<SupplierReturnResponse> => {
  const response = await api.get<SupplierReturnResponse>(`${API_URL}/${id}`);
  return response.data;
};

// Create new supplier return
export const createSupplierReturn = async (data: SupplierReturnCreate): Promise<SupplierReturnResponse> => {
  const response = await api.post<SupplierReturnResponse>(API_URL, data);
  return response.data;
};

// Update supplier return
export const updateSupplierReturn = async (
  id: number,
  data: SupplierReturnUpdate
): Promise<SupplierReturnResponse> => {
  const response = await api.patch<SupplierReturnResponse>(`${API_URL}/${id}`, data);
  return response.data;
};

// Submit supplier return (change status to SUBMITTED)
export const submitSupplierReturn = async (id: number): Promise<SupplierReturnResponse> => {
  const response = await api.post<SupplierReturnResponse>(`${API_URL}/${id}/submit`);
  return response.data;
};

// Approve supplier return
export const approveSupplierReturn = async (id: number, notes?: string): Promise<SupplierReturnResponse> => {
  const response = await api.post<SupplierReturnResponse>(`${API_URL}/${id}/approve`, { notes });
  return response.data;
};

// Reject supplier return
export const rejectSupplierReturn = async (id: number, notes: string): Promise<SupplierReturnResponse> => {
  const response = await api.post<SupplierReturnResponse>(`${API_URL}/${id}/reject`, { notes });
  return response.data;
};

// Complete supplier return
export const completeSupplierReturn = async (id: number): Promise<SupplierReturnResponse> => {
  const response = await api.post<SupplierReturnResponse>(`${API_URL}/${id}/complete`);
  return response.data;
};
